import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Alert, Table, Spinner } from 'react-bootstrap';
import apiService from '../../services/api';
import { formatColumnValue } from './recordColumnUtils';
import TicketCombobox from './TicketCombobox';

const RestoreRecordModal = ({ show, onHide, record, columns, recordLabel, organization, restoreRecord, onRestored }) => {
    const [ticketRef, setTicketRef] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [tickets, setTickets] = useState([]);
    const [loadingTickets, setLoadingTickets] = useState(false);
    const [ticketsError, setTicketsError] = useState('');

    const onHideRef = useRef(onHide);
    useEffect(() => {
        onHideRef.current = onHide;
    });

    useEffect(() => {
        if (show) {
            setTicketRef('');
            setSubmitted(false);
            setSubmitError('');
            loadTickets();
        }
    }, [show]);

    useEffect(() => {
        if (!submitted) return;
        const timer = setTimeout(() => onHideRef.current(), 2500);
        return () => clearTimeout(timer);
    }, [submitted]);

    const loadTickets = async () => {
        setLoadingTickets(true);
        setTicketsError('');

        try {
            const response = await apiService.getTickets(organization?.id);
            if (response.success) {
                setTickets(Array.isArray(response.data) ? response.data : []);
            } else {
                setTicketsError(response.error || 'Failed to load tickets');
            }
        } catch (error) {
            console.error('Error loading tickets:', error);
            setTicketsError('Failed to load tickets');
        } finally {
            setLoadingTickets(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setSubmitError('');

        try {
            const response = await restoreRecord(record.id, ticketRef.trim());
            if (response.success) {
                setSubmitted(true);
                onRestored?.();
            } else {
                setSubmitError(response.error || 'Failed to restore record');
            }
        } catch (error) {
            console.error('Error restoring record:', error);
            setSubmitError('Failed to restore record');
        } finally {
            setSubmitting(false);
        }
    };

    if (!record) return null;

    const noTicketsFound = !loadingTickets && !ticketsError && tickets.length === 0;

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-arrow-counterclockwise me-2"></i>
                    Restore {recordLabel}
                </Modal.Title>
            </Modal.Header>
            {submitted ? (
                <>
                    <Modal.Body>
                        <Alert variant="success" className="mb-0">
                            <i className="bi bi-check-circle me-2"></i>
                            {recordLabel} restored successfully for ticket <strong>{ticketRef}</strong>.
                        </Alert>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={onHide}>Close</Button>
                    </Modal.Footer>
                </>
            ) : (
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <h6 className="mb-3">Record Summary</h6>
                        <Table size="sm" bordered className="mb-4">
                            <tbody>
                                {columns.map(col => (
                                    <tr key={col.key}>
                                        <th style={{ width: '35%' }}>{col.label}</th>
                                        <td>{formatColumnValue(record[col.key], col)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        <Form.Group>
                            <Form.Label>Ticket Number *</Form.Label>
                            {ticketsError && (
                                <Alert variant="warning" className="py-2">
                                    <i className="bi bi-exclamation-triangle me-2"></i>{ticketsError}
                                </Alert>
                            )}
                            {loadingTickets ? (
                                <div className="text-muted py-2">
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Loading tickets...
                                </div>
                            ) : noTicketsFound ? (
                                <Alert variant="info" className="py-2 mb-0">
                                    <i className="bi bi-info-circle me-2"></i>
                                    No tickets found for this organization.
                                </Alert>
                            ) : (
                                <TicketCombobox
                                    tickets={tickets}
                                    value={ticketRef}
                                    onChange={setTicketRef}
                                    placeholder="Type or select a ticket number"
                                />
                            )}
                        </Form.Group>
                        {submitError && (
                            <Alert variant="danger" className="mt-3 mb-0">
                                <i className="bi bi-exclamation-triangle me-2"></i>{submitError}
                            </Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={onHide} disabled={submitting}>Cancel</Button>
                        <Button variant="primary" type="submit" disabled={!ticketRef.trim() || noTicketsFound || submitting}>
                            {submitting ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                                    Implementing...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-arrow-counterclockwise me-2"></i>
                                    Implement Restore Request
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            )}
        </Modal>
    );
};

export default RestoreRecordModal;
